import React, { FC, useState, useEffect, useMemo, Fragment } from 'react';
import { getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import { Alert } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { WorkingDayBeforeCounter } from 'utils/internal-delivery-advance-working-date';

type IntExtDeliveryDatePickerProps = {
    onChangeDate: (date: Date | null) => void;
    // The GraphQL Date scalar reaches the client as an ISO string; callers pass it on as is.
    selectedDate?: Date | string | null;
    deliveryDaysAdvance?: number;
    clinicDeliveryDate?: number;
    isExternal?: boolean;
    label?: string;
    dataTest?: string;
};

const IntExtDeliveryDatePicker: FC<IntExtDeliveryDatePickerProps> = ({
    onChangeDate,
    selectedDate,
    deliveryDaysAdvance,
    clinicDeliveryDate,
    isExternal,
    label,
    dataTest,
}: IntExtDeliveryDatePickerProps) => {
    const [date, setDate] = useState<Date | null>(null);
    const [workingDaysBeforeRequest, setWorkingDaysBeforeRequest] = useState<number>();
    const { t: translate } = useTranslation(['common', 'orders']);
    // Stable bounds: a fresh `new Date()` every render (now, later in the day than a clinic
    // date at midnight) gave the picker an empty range that changed on each render (DD-3648).
    const minDate = useMemo(() => new Date(new Date().setHours(0, 0, 0, 0)), []);
    const maxDate = useMemo(() => new Date(clinicDeliveryDate as number), [clinicDeliveryDate]);

    useEffect(() => {
        // Always hold a Date: the change handler compares with getTime(), which a string
        // lacks, so every pick threw and the deadline never changed.
        setDate(selectedDate ? new Date(selectedDate) : null);
    }, [selectedDate]);

    useEffect(() => {
        if (deliveryDaysAdvance || deliveryDaysAdvance === 0) {
            setWorkingDaysBeforeRequest(deliveryDaysAdvance);
        }
    }, [deliveryDaysAdvance]);

    const handleChange = (pickedDate: Date | null) => {
        const newDate = pickedDate ? new Date(new Date(pickedDate).setHours(12, 0, 0, 0)) : null;
        // An echo of the date already shown changes nothing; passing it on re-rendered the step (DD-3648).
        if ((newDate?.getTime() ?? null) === (date?.getTime() ?? null)) {
            return;
        }
        setDate(newDate);
        onChangeDate(newDate);
        if (newDate && (deliveryDaysAdvance || deliveryDaysAdvance === 0)) {
            const deliveryDate = new Date(clinicDeliveryDate as number);
            const workingDayCount = WorkingDayBeforeCounter(deliveryDate, newDate);
            setWorkingDaysBeforeRequest(workingDayCount);
        }
    };

    return (
        <Fragment>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                <DatePicker
                    format={getDatePickerFormat()}
                    minDate={minDate}
                    maxDate={maxDate}
                    label={label ? label : translate('DATE')}
                    value={date}
                    onChange={handleChange}
                    slotProps={{
                        textField: { fullWidth: true, margin: 'dense', inputProps: { 'data-test': dataTest } },
                    }}
                />
            </LocalizationProvider>
            {date && (workingDaysBeforeRequest || workingDaysBeforeRequest === 0) ? (
                <Alert severity="info" variant="outlined" sx={{ border: 0 }}>
                    {`${workingDaysBeforeRequest} ${translate(
                        'orders:WORKING_DAYS_BEFORE_CLINIC_REQUESTED_DELIVERY_DATE',
                    )}`}
                </Alert>
            ) : undefined}
            {!date ? (
                isExternal ? (
                    <Alert severity="info" variant="outlined" sx={{ border: 0 }}>
                        {translate('orders:NO_EXTERNAL_DELIVERY_DATE_SELECTED')}
                    </Alert>
                ) : (
                    <Alert severity="info" variant="outlined" sx={{ border: 0 }}>
                        {translate('orders:NO_INTERNAL_DELIVERY_DATE_SELECTED')}
                    </Alert>
                )
            ) : undefined}
        </Fragment>
    );
};

export default IntExtDeliveryDatePicker;
